<template>
  <div class="position-relative bgray-100 vh-100" style="min-height: 490px">
    <div
      :class="[
        'bg-white w-100 h-100 shadow-sm report-header',
        { collapsed: content_list_open && show_gri_table }
      ]"
    >
      <div class="d-flex justify-content-between p-3 border-bottom report-header-nav">
        <div class="d-flex align-items-center">
          <template v-if="logo_url">
            <b-img :src="logo_url" class="mr-3" style="max-height: 40px" />
          </template>
          <div class="fw-400 h6 gray-600 mb-0">
            {{ lang !== 'ENG' ? selected_report.report_name : selected_report.report_name_e }}
          </div>
        </div>

        <div
          v-show="theme_option.show_printing_btn || theme_option.show_translation_btn || is_viewer"
        >
          <template v-if="theme_option.show_printing_btn && !selected_report.sample_report">
            <span v-b-tooltip.hover :title="pdfReadyTooltip" class="d-inline-block">
              <b-button
                :disabled="!pdfReady || isPrinting"
                variant="dark-border"
                size="lg"
                class="p-0 mr-2 preview-tool-btn"
                :style="{ 'pointer-events': !pdfReady || isPrinting ? 'none' : 'auto' }"
                @click="handlePrintClick"
              >
                <b-spinner v-if="isPrinting" small />
                <fa v-else icon="print" />
              </b-button>
            </span>
          </template>

          <template v-if="theme_option.show_translation_btn">
            <span v-b-tooltip.hover :title="translateBtnTooltip" class="d-inline-block">
              <b-button
                :disabled="!pdfReady || isLangSwitching"
                variant="dark-border"
                size="lg"
                class="p-0 mr-2 preview-tool-btn"
                :style="{
                  'pointer-events': !pdfReady || isLangSwitching ? 'none' : 'auto'
                }"
                @click="toggleLang"
              >
                <b-spinner v-if="isLangSwitching" small />
                <fa v-else icon="language" />
              </b-button>
            </span>
          </template>

          <template v-if="!is_viewer">
            <b-button
              v-b-tooltip.hover
              variant="dark-border"
              size="lg"
              class="p-0 preview-tool-btn"
              :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'"
              @click="$router.push({ name: 'CheckReport' })"
            >
              <fa icon="times" />
            </b-button>
          </template>
        </div>
      </div>

      <b-modal
        v-model="loadingModalVisible"
        centered
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        hide-footer
        size="sm"
        :title="loadingModalTitle"
      >
        <div class="py-2">
          <template v-if="isPrinting">
            <div class="mb-3">
              <div
                v-for="(step, i) in printStepLabels"
                :key="'print-' + i"
                class="d-flex align-items-center mb-2"
              >
                <b-icon
                  v-if="printCurrentStep > i + 1"
                  icon="check-circle-fill"
                  variant="success"
                  class="mr-2"
                />
                <b-spinner
                  v-else-if="printCurrentStep === i + 1"
                  small
                  variant="primary"
                  class="mr-2"
                />
                <b-icon v-else icon="circle" class="text-muted mr-2" />
                <span :class="{ 'text-muted': printCurrentStep < i + 1 }">{{ step }}</span>
              </div>
            </div>
            <b-progress
              :max="printTotalSteps"
              :value="printCurrentStep > printTotalSteps ? printTotalSteps : printCurrentStep"
              variant="primary"
              animated
              height="6px"
            />
          </template>
          <template v-else-if="isLangSwitching">
            <div class="mb-3">
              <div
                v-for="(step, i) in langSwitchStepLabels"
                :key="i"
                class="d-flex align-items-center mb-2"
              >
                <b-icon
                  v-if="langSwitchStep > i + 1"
                  icon="check-circle-fill"
                  variant="success"
                  class="mr-2"
                />
                <b-spinner
                  v-else-if="langSwitchStep === i + 1"
                  small
                  variant="primary"
                  class="mr-2"
                />
                <b-icon v-else icon="circle" class="text-muted mr-2" />
                <span :class="{ 'text-muted': langSwitchStep < i + 1 }">{{ step }}</span>
              </div>
            </div>
            <b-progress
              :max="3"
              :value="langSwitchStep > 3 ? 3 : langSwitchStep"
              variant="primary"
              animated
              height="6px"
              class="mb-2"
            />
            <p class="mb-0 text-center text-muted f-85">{{ langSwitchDirectionLabel }}</p>
          </template>
        </div>
      </b-modal>

      <div class="report-header-body">
        <div
          class="d-flex flex-column justify-content-center flex-grow-1 py-4 px-5"
          style="height: 50%; min-height: 300px"
        >
          <h1
            class="fw-600 display-4 line-height-sm mb-0 pl-3"
            :style="`font-size: ${theme_option?.title_size}rem; font-family:${selected_report.font_family}; border-left: 8px solid ${selected_report.main_color};`"
          >
            {{ lang !== 'ENG' ? selected_report.report_name : selected_report.report_name_e }}
          </h1>
          <h5 class="fw-400 gray-600 mb-0 pl-4">
            {{ selected_report.start_date }} ~ {{ selected_report.end_date }}
          </h5>
        </div>

        <div class="mb-3 px-5">
          <b-button-group>
            <b-button
              v-if="active_gri_table"
              :variant="show_gri_table ? 'dark-border-selected' : 'dark-border'"
              size="lg"
              :disabled="show_gri_table"
              @click="$emit('showTableView', 'GRI')"
            >
              GRI
            </b-button>
            <b-button
              v-if="active_sasb_table"
              :variant="show_sasb_table ? 'dark-border-selected' : 'dark-border'"
              size="lg"
              :disabled="show_sasb_table"
              @click="$emit('showTableView', 'SASB')"
            >
              SASB
            </b-button>
            <b-button
              v-if="active_tcfd_table"
              :variant="show_tcfd_table ? 'dark-border-selected' : 'dark-border'"
              size="lg"
              :disabled="show_tcfd_table"
              @click="$emit('showTableView', 'TCFD')"
            >
              TCFD
            </b-button>
            <b-button
              v-if="active_dc_table"
              :variant="show_dc_table ? 'dark-border-selected' : 'dark-border'"
              size="lg"
              :disabled="show_dc_table"
              @click="$emit('showTableView', 'DATA_CENTER')"
            >
              Data Center
            </b-button>
          </b-button-group>

          <div v-if="$route.hash" class="mt-5">
            <b-button @click="navAnchor(null)">
              <b-icon-menu-button-fill />
              {{ lang !== 'ENG' ? '전체보기' : 'Show All' }}
            </b-button>
          </div>
        </div>

        <template v-if="lang !== 'ENG'">
          <div
            class="d-flex align-items-center flex-grow-1 pb-3 contents-table"
            :style="[{ backgroundColor: selected_report.main_color }]"
          >
            <div
              v-for="(category, category_index) in report_categories"
              :key="`category-${category.category_id}-${category_index}`"
              :class="['contents-table-column py-4 px-5']"
              :style="[{ width: `${100 / report_categories.length}%` }]"
            ></div>
          </div>
        </template>

        <template v-else>
          <div
            class="d-flex align-items-top flex-grow-0 py-4 contents-table"
            :style="[{ backgroundColor: selected_report.main_color }]"
          >
            <div
              v-for="(category, category_index) in report_categories"
              :key="`category-${category.category_id}-${category_index}`"
              :class="['contents-table-column pt-3 pb-5 px-5']"
              :style="[{ width: `${100 / report_categories.length}%` }]"
            >
              <div v-if="show_gri_table">
                <div class="contents-table-divider" style=""></div>
                <div class="d-flex justify-content-between align-items-top">
                  <h4 class="text-white mb-0 contents-table-title">
                    {{ category_index + 1 }}. {{ category.category_name_e }}
                  </h4>
                  <div
                    class="cursor contents-table-link"
                    @click="navAnchor('#rp' + category.category_id)"
                  >
                    <fa icon="arrow-down" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="show_gri_table"
      :class="['report-table-contents', 'px-3 py-3', { collapsed: !content_list_open }]"
    >
      <div class="d-flex justify-content-between align-items-end border-bottom pb-4 mx-3 mb-4 pt-2">
        <h3 class="fw-600 mb-0">Table of Contents</h3>
        <div class="text-right f-95">
          <b-badge class=""> M </b-badge>
          <span class=""
            >: {{ lang !== 'ENG' ? '중요주제 (Material Topic)' : 'Material Topic' }}
          </span>
        </div>
      </div>

      <ContentsListNoImage
        class="report-table-contents-body"
        :preview_mode="true"
        :web_view="true"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import * as ih from '@/components/util';
import ContentsListNoImage from '@/components/report_preview/gri2021_viewer/viewer_components/ContentsListNoImage.vue';
import PrintSb from '@/plugins/print_plugin/printareaSb.js';

export default {
  name: 'ViewerHeaderNoImageTheme',
  components: {
    ContentsListNoImage
  },
  props: {
    theme_option: {
      type: Object,
      required: true
    },
    dark_logo_url: {
      required: true,
      type: [String, Boolean]
    },
    content_list_open: {
      required: true,
      type: Boolean
    },
    main_bg_color_gradient: {
      required: true,
      type: [String, Object]
    },
    active_gri_table: {
      type: Boolean,
      default: () => false
    },
    active_sasb_table: {
      type: Boolean,
      default: () => false
    },
    active_tcfd_table: {
      type: Boolean,
      default: () => false
    },
    show_gri_table: {
      type: Boolean,
      default: () => false
    },
    show_sasb_table: {
      type: Boolean,
      default: () => false
    },
    show_tcfd_table: {
      type: Boolean,
      default: () => false
    },
    active_dc_table: {
      type: Boolean,
      default: false
    },
    show_dc_table: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPrinting: false,
      printCurrentStep: 0,
      printTotalSteps: 3,
      loadingModalVisible: false,
      isLangSwitching: false,
      langSwitchStep: 0,
      langSwitchFromLang: ''
    };
  },
  computed: {
    ...mapGetters(['is_viewer']),
    ...mapState(['static_host']),
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapGetters('gri2021_report_view', ['report_categories']),
    ...mapState('documents', ['document_list_loaded']),
    ...mapState('data_tables', ['table_list_loaded']),
    ...mapState('business_cases', ['business_case_loaded']),
    ...mapState('gri2021_report_view', ['report_contents_loaded']),
    ...mapState('assurance', ['assurance_list_loaded']),
    pdfReady() {
      return (
        this.document_list_loaded &&
        this.table_list_loaded &&
        this.business_case_loaded &&
        this.report_contents_loaded &&
        this.assurance_list_loaded
      );
    },
    printStepLabels() {
      if (this.lang !== 'ENG') {
        return ['콘텐츠 준비', '이미지 로딩', '인쇄 준비'];
      }
      return ['Preparing content', 'Loading images', 'Preparing print'];
    },
    langSwitchStepLabels() {
      if (this.langSwitchFromLang === 'ENG') {
        return ['Checking translation data', 'Converting content', 'Rendering'];
      }
      return ['번역 데이터 확인', '콘텐츠 변환', '화면 렌더링'];
    },
    langSwitchDirectionLabel() {
      return this.langSwitchFromLang === 'KOR' ? '한국어 → English' : 'English → 한국어';
    },
    loadingModalTitle() {
      if (this.isPrinting) {
        return this.lang !== 'ENG' ? 'PDF 출력 준비중' : 'Preparing PDF';
      }
      if (this.isLangSwitching) {
        return this.langSwitchFromLang !== 'ENG' ? '언어 전환중' : 'Switching Language';
      }
      return '';
    },
    translateBtnTooltip() {
      if (!this.pdfReady) {
        return this.lang !== 'ENG' ? '보고서 준비중입니다.' : 'Preparing report...';
      }
      return this.lang !== 'ENG' ? '영문보기' : 'Korean';
    },
    pdfReadyTooltip() {
      if (this.isPrinting) {
        return this.lang !== 'ENG' ? 'pdf 출력 준비중...' : 'Preparing PDF...';
      }
      if (this.pdfReady) {
        return this.lang !== 'ENG' ? '출력하기' : 'Print to PDF';
      }
      return this.lang !== 'ENG' ? 'pdf 출력을 준비중입니다.' : 'Preparing PDF...';
    },
    popTitle() {
      if (this.lang === 'KOR') {
        return this.selected_report.report_name;
      } else {
        return this.selected_report.report_name_e;
      }
    },
    logo_url() {
      return !this.selected_report.logo_path
        ? null
        : this.static_host + this.selected_report.logo_path;
    }
  },
  methods: {
    ...mapMutations('report_list', ['change_lang']),

    async toggleLang() {
      if (this.isLangSwitching) return;
      this.isLangSwitching = true;
      this.langSwitchStep = 0;
      this.langSwitchFromLang = this.lang;
      this.loadingModalVisible = true;

      try {
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(150);

        this.langSwitchStep = 1;
        await this.delay(500);

        this.langSwitchStep = 2;
        await this.delay(300);

        if (this.langSwitchFromLang === 'KOR') {
          this.change_lang('ENG');
          document.querySelector('title').textContent = this.selected_report.report_name_e;
          ih.updateParamsFromQueryString('l', 'ENG', false);
        } else {
          this.change_lang('KOR');
          document.querySelector('title').textContent = this.selected_report.report_name;
          ih.updateParamsFromQueryString('l', null, false);
        }

        this.langSwitchStep = 3;
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(500);
      } finally {
        this.isLangSwitching = false;
        this.loadingModalVisible = false;
        this.langSwitchStep = 0;
      }
    },
    navAnchor(anchor) {
      ih.anchorGo(anchor);
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async handlePrintClick() {
      if (!this.pdfReady || this.isPrinting) return;

      this.isPrinting = true;
      this.printCurrentStep = 0;
      this.loadingModalVisible = true;

      try {
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(150);

        this.printCurrentStep = 1;
        await this.$nextTick();
        await this.delay(600);

        this.printCurrentStep = 2;
        await this.waitForImages();
        await this.delay(600);

        this.printCurrentStep = 3;
        await this.delay(400);

        this.loadingModalVisible = false;

        new PrintSb({
          ids: '#full-report-preview',
          popTitle: this.popTitle,
          endCallback: () => {
            this.isPrinting = false;
            this.printCurrentStep = 0;
          }
        });
      } finally {
        this.isPrinting = false;
        this.printCurrentStep = 0;
        this.loadingModalVisible = false;
      }
    },
    waitForImages() {
      return new Promise(resolve => {
        const printElement = document.querySelector('#full-report-preview');
        if (!printElement) {
          resolve();
          return;
        }
        const images = Array.from(printElement.querySelectorAll('img'));
        const allLoaded = images.length === 0 || images.every(img => img.complete);
        if (allLoaded) {
          resolve();
          return;
        }
        let loaded = 0;
        const total = images.filter(img => !img.complete).length;
        const onLoad = () => {
          loaded++;
          if (loaded >= total) resolve();
        };
        images.forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', onLoad, { once: true });
            img.addEventListener('error', onLoad, { once: true });
          }
        });
        setTimeout(resolve, 10000);
      });
    }
  }
};
</script>

<style scoped lang="scss">
.report-header {
  min-height: 410px;

  &:not(.collapsed) {
    @media screen and (max-height: 649.98px) {
      overflow-y: auto;
    }

    min-height: 460px;
  }

  &.collapsed {
    overflow-y: hidden;

    .report-header-body {
      transition-duration: 0.5s;
      transition-delay: 0s;
      opacity: 0;
    }
  }

  &-body {
    display: flex;
    flex-direction: column;
    height: calc(100% - 85.8px);

    transition-property: opacity;
    transition-duration: 0.5s;
    transition-delay: 0.5s;
    transition-timing-function: ease-in-out;
    opacity: 1;
  }
}

.report-table-contents {
  position: absolute;
  top: calc(85.8px + 1.5rem);
  width: calc(100% - 3rem);
  max-height: 100vh;

  display: flex;
  flex-direction: column;
  transition-property: opacity, z-index, transform;
  transition-duration: 0.6s, 0.3s, 0.6s;
  transition-delay: 0.3s, 0s, 0s;
  transition-timing-function: ease, ease, ease;
  opacity: 1;
  z-index: 1;
  overflow-y: hidden;
  height: 100%;
  min-height: 345px;
  transform: translateY(0px);

  &.collapsed {
    transition-duration: 0.5s, 0.3s, 0.3s;
    transition-delay: 0s, 0.7s, 0.3s;
    opacity: 0;
    z-index: -1;
    transform: translateY(-30px);
  }

  &-body {
    max-height: calc(100vh - 37.75px - 14.5rem);
    min-height: 240px;
    height: 100%;
    overflow-y: auto;
  }

  &-list {
    max-height: calc(100% - 61px);
    height: 100%;
  }
}

.contents-table {
  &-link {
    transition: margin-bottom 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-bottom: 0px;
    color: white;
    flex-grow: 0;
    flex-shrink: 0;

    &:hover {
      border-width: 2px;
      border-color: #f8f9f9;
      border-style: solid;
      margin-bottom: 1px;
    }
  }

  &-divider {
    height: 3px;
    background-color: white;
    margin-top: 10px;
    margin-bottom: 7px;
  }

  &-title_en {
    color: #f8f9fa;
  }
}
</style>
